import { useRouter } from "next/router";
import React, { useEffect, useState, useMemo } from "react";
import { Wine } from "../../../context/wineContext/reducer";
import { formatDate } from "../../../utils/date";
import { numberFormattedWithCurrency } from "../../../utils/format";
import { formatScore } from "../../../utils/wine";
import ReadMore from "../Buttons/ReadMore";
import LockedContentWrapper from "../Wrappers/LockedContentWrapper";
import TastingNote from "./elements/TastingNote";
import { useAuth } from "../../../context/authContext";
import Link from "next/link";

type WineVintageProps = {
  wine: Wine,
  slug?: string,
  open?: boolean,
  useBaseWineId?: boolean,
  producerName?: string,
  showProducerCommentary?: boolean
};

const WineVintage = ({ wine, slug, open, useBaseWineId, producerName, showProducerCommentary }: WineVintageProps) => {
  const router = useRouter();
  const {state: { user }} = useAuth();
  const [hover, setHover] = useState(false);
  const [openState, setOpenState] = useState(open || false);
  const [baseWinePrice, setBaseWinePrice] = useState<{price: string, vintage: number}>();

  useEffect(() => {
    setOpenState(open || false)
  }, [open])

  const wineUrl = useMemo(() => {
    if (useBaseWineId && wine.base_wine_id) {
      return `/wines/${wine.base_wine_id}/${wine.vintage}?wineId=${wine.id}`;
    }

    // Some callers only have a wine slug; the destination page can resolve it to base wine slug.
    const resolvedSlug = slug || wine?.slug;
    return `/wines/${resolvedSlug}/${wine?.vintage}`;
  }, [useBaseWineId, wine.base_wine_id, wine.id, wine.vintage, wine.slug, slug]);

  const reviewDate = React.useMemo(() => {
    if (wine) {
      if (wine.tasted_on) {
        return wine.tasted_on;
      } else if (wine.article_date) {
        return wine.article_date;
      } else {
        return formatDate(new Date(wine.review_date), "MMMM yyyy")
      }
    }

  }, [wine]);

  const price = React.useMemo(() => {
    if (!wine) {
      return "0";
    }
  
    if (wine?.release_price && parseInt(wine.release_price) > 0) {
      return wine.release_price;
    }
  
    if (wine.price && parseInt(wine.price) > 0) {
      return wine.price;
    }
    
    if (wine?.base_wine_prices?.length > 0 && process.env.NEXT_PUBLIC_BASE_WINE_PRICE_ENABLED === "true") {
      const vintage = wine.vintage;
      const baseWinePrice = wine.base_wine_prices.find(
        (baseWinePrice) => baseWinePrice.vintage === vintage || baseWinePrice.vintage < vintage
      );
      if (parseInt(baseWinePrice?.price) <= 10) return "0";

      if (baseWinePrice && vintage !== baseWinePrice?.vintage) setBaseWinePrice(baseWinePrice);
      return baseWinePrice?.price || "0";
    }
    return "0";
  }, [wine]);

  const redirectToSignIn = () => {
    router.push({pathname: "/users/sign_in", query: { from: router.asPath}})
  }

  return (
    wine && <div
      className="border-b last:border-0 cursor-pointer"
      role="group"
      aria-label="Wine Review"
    >
      <div className="flex flex-col md:flex-row items-center"
      >
        <div className="w-full md:pb-2"
          onMouseEnter={() => { setHover(true) }}
          onMouseLeave={() => { setHover(false) }}
          onClick={() => { router.push(wineUrl) }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              router.push(wineUrl)
            }
          }}
        >
          <h1 className={`transition text-lg font-bold ${ hover && 'text-red' } mb-0 mt-4` }>
            { wine.vintage == 0 ? "NV" : wine.vintage} { wine.wine_name }
          </h1>
          <div>
            {
              producerName &&
                <p className="font-light text-sm">Producer: <b>{ producerName }</b></p>
            }
            {parseInt(price) > 0 && (
              <p className="font-light text-sm">
                Release Price:{" "}
                <b>
                  {numberFormattedWithCurrency("$", parseInt(price))}{" "}
                  {baseWinePrice && `(${baseWinePrice.vintage})`}
                </b>
              </p>
            )}
            {
              wine.color &&
              <p className="font-light text-sm">Color: <b>{ wine.color }</b></p>
            }
          </div>
        </div>
        <div className="w-full md:w-1/2 md:text-right pr-4 wine-score-paywall">
          <LockedContentWrapper
            alignment="end"
            message={
              <div className="w-12 hidden md:block flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-12 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            }
            placeholderContent={
              <p className={`font-bold text-red opacity-50`}>
                00
              </p>
            }
          >
            <div className="flex flex-col-reverse md:flex-col"
              onMouseEnter={() => { setHover(true) }}
              onMouseLeave={() => { setHover(false) }}
              onClick={() => { router.push(wineUrl) }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if(e.key === "Enter") {
                  router.push(wineUrl)
                }
              }}
            >
              <div className="text-xl">
                <span className="text-red font-bold">{ formatScore(wine.score_raw) }</span>
              </div>
              {
                wine.drinking_window_begin > 0 &&
                <p className="font-light text-sm">Drinking Window: <b>{ wine.drinking_window_begin } - { wine.drinking_window_end }</b></p>
              }
              {
                wine.author != undefined &&
                <p className="font-light text-sm"><b>{ wine.author }, { reviewDate }</b></p>
              }
            </div>
          </LockedContentWrapper>
        </div>
        <div
          className="transition hover:bg-gray-100 p-1 rounded-full hover:text-red"
          onClick={(e) => {
            e.stopPropagation()
            setOpenState(!openState)
          }}
          onKeyDown={(e) => {
            if(e.key === "Enter") {
              e.stopPropagation()
              setOpenState(!openState)
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Toggle ${openState ? "Close" : "Open"} Vintage`}
        >
          <svg className={`transition-all transform ${openState ? "rotate-180" : "rotate-0"}`} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div className={`transition-all duration-500 overflow-hidden wine-review-paywall ${openState ? 'max-h-96' : 'max-h-0'}`}>
        <LockedContentWrapper
          message={
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="pt-1 pl-3 text-2xl">
                  Subscriber Access Only
                </p>
              </div>
              {!user ? (
                <p className="text-sm">
                  <button
                    onClick={() => redirectToSignIn()}
                    className="text-red"
                    onKeyDown={(e) => {
                      if(e.key === "Enter") {
                        redirectToSignIn()
                      }
                    }}
                    tabIndex={0}
                  >
                    Log In
                  </button>{" "}
                  or{" "}
                  <Link
                    href="/users/plan"
                    className="text-red"
                  >
                    Sign Up
                  </Link>
                </p>
              ) : (
                <p className="text-sm">
                  <Link href="/users/edit" className="text-red">
                    Renew or Subscribe
                  </Link>{" "}
                  to access this content{" "}
                </p>
              )}
            </div>
          }
          placeholderContent={
            <div className="p-2">
              <p className="italic text-sm text-red pb-2"><button>You&#39;ll Find The Article Name Here</button></p>
              <p className="pb-2">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer vitae aliquam odio. Aliquam purus diam, tempor et consectetur vitae, eleifend ac quam. Proin nec mauris ac odio iaculis semper. Integer posuere pharetra aliquet. Nullam tincidunt sagittis est in maximus. Donec sem orci, vulputate ac quam non, consectetur fermentum diam. In dignissim magna id orci dignissim convallis. Integer sit amet placerat dui. Aliquam pharetra ornare nulla at vulputate. Sed dictum, mi eget fringilla lacinia, nisl tortor condimentum mi, vitae ultrices quam diam ac neque. Donec hendrerit vulputate felis, fringilla varius massa.
              </p>
              <p className="opacity-40 italic text-sm">- By Author Name, Month Date, Year</p>
            </div>
          }
        >
          {
            wine.tasting_note && 
            <div
              onMouseEnter={() => { setHover(true) }}
              onMouseLeave={() => { setHover(false) }}
              onClick={() => { router.push(wineUrl) }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if(e.key === "Enter") {
                  router.push(wineUrl)
                }
              }}
            >
              <TastingNote 
              renderByline={false}
              review={wine}
              markupClassnames="transition pb-2 font-light pt-1"
            />
            </div>
            
          }
        </LockedContentWrapper>

        <ReadMore
          className="pt-2"
          link={wineUrl}
          needLink={true}
        />
      </div>
    </div>
  )
}

export default WineVintage;
